import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

export interface PriceChangeRecord {
  id: string;
  shopifyVariantId: string;
  shopifyProductId: string;
  sku: string | null;
  productTitle: string | null;
  variantTitle: string | null;
  productVendor: string | null;
  productType: string | null;
  oldPrice: number;
  newPrice: number;
  oldCaseCost: number | null;
  newCaseCost: number | null;
  margin: number | null;
  source: string;
  labelPrinted: boolean;
  labelPrintedAt: string | null;
  createdAt: string;
}

export interface PriceLabelFilters {
  printed?: string;
  vendor?: string;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface FilterOptions {
  vendors: string[];
  types: string[];
}

export function usePriceLabels() {
  const authFetch = useAuthenticatedFetch();
  const [items, setItems] = useState<PriceChangeRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ vendors: [], types: [] });

  const loadLabels = useCallback(
    async (filters: PriceLabelFilters = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (filters.printed != null) params.set("printed", filters.printed);
        if (filters.vendor) params.set("vendor", filters.vendor);
        if (filters.type) params.set("type", filters.type);
        if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.set("dateTo", filters.dateTo);
        if (filters.search) params.set("search", filters.search);
        if (filters.page) params.set("page", String(filters.page));
        if (filters.pageSize) params.set("pageSize", String(filters.pageSize));

        const res = await authFetch(`/api/price-labels?${params.toString()}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);

        setItems(json.data || []);
        setTotal(json.total || 0);
        setFilterOptions(json.filterOptions || { vendors: [], types: [] });
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  const printLabels = useCallback(
    async (ids: string[]): Promise<Blob | null> => {
      try {
        const res = await authFetch("/api/price-labels/print", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        });

        if (!res.ok) {
          const json = await res.json();
          throw new Error(json.error);
        }

        return await res.blob();
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const deleteLabel = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/price-labels/${id}`, { method: "DELETE" });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  return { items, total, loading, error, filterOptions, loadLabels, printLabels, deleteLabel };
}
